import backgroundHome from 'assets/images/background-home.jpg'
import exercise from 'assets/images/exercise.jpg'
import freestyleSetup from 'assets/images/freestyle-setup.jpg'
import freestyleTraining from 'assets/images/freestyle-training.jpg'
import quickstart from 'assets/images/quickstart.jpg'
import { INRWorkoutSession, WorkoutName } from 'interfaces/workout'

export const MAX_FAVORITE_WORKOUTS = 8

export const FREESTYLE_FAVORITE_IMAGES = {
  'freestyle-setup': freestyleSetup,
  'freestyle-training': freestyleTraining,
  exercise,
  quickstart,
  'background-home': backgroundHome,
} as const

export type FreestyleFavoriteImageKey = keyof typeof FREESTYLE_FAVORITE_IMAGES

export interface TemplateFavoriteWorkout {
  favoriteId: string
  kind: 'template'
  templateId: string
}

export interface FreestyleFavoriteWorkout {
  favoriteId: string
  favoriteImage: string
  imageKey: FreestyleFavoriteImageKey
  kind: 'freestyle'
  workoutSession: INRWorkoutSession
}

export type FavoriteWorkout = TemplateFavoriteWorkout | FreestyleFavoriteWorkout

export interface ApiTabletWorkoutFavorite {
  id: string
  favoriteId: string
  kind: string
  templateId?: string | null
  imageKey?: string | null
  workoutSession?: string | null
}

export interface TabletWorkoutFavoriteInput {
  kind: 'template' | 'freestyle'
  templateId?: string
  favoriteId?: string
  imageKey?: FreestyleFavoriteImageKey
  workoutSession?: string
}

export interface ToggleFavoriteResult {
  favorite: FavoriteWorkout | null
  added: boolean
  limitReached: boolean
}

const isFreestyleImageKey = (value?: string | null): value is FreestyleFavoriteImageKey =>
  Boolean(value && Object.prototype.hasOwnProperty.call(FREESTYLE_FAVORITE_IMAGES, value))

export const sanitizeFavoriteSession = (session: INRWorkoutSession): INRWorkoutSession => ({
  ...session,
  id: null,
  rounds: session.rounds.map((round) => ({
    ...round,
    exercises: round.exercises.map((exercise) => ({
      ...exercise,
      hits: [],
      started: null,
    })),
  })),
})

const pickFreestyleImageKey = (
  favorites: FavoriteWorkout[],
  random: () => number,
): FreestyleFavoriteImageKey => {
  const imageKeys = Object.keys(FREESTYLE_FAVORITE_IMAGES) as FreestyleFavoriteImageKey[]
  const usedImages = new Set(
    favorites
      .filter((favorite): favorite is FreestyleFavoriteWorkout => favorite.kind === 'freestyle')
      .map((favorite) => favorite.imageKey),
  )
  const unusedImages = imageKeys.filter((imageKey) => !usedImages.has(imageKey))
  const imagePool = unusedImages.length ? unusedImages : imageKeys
  const randomIndex = Math.min(Math.floor(random() * imagePool.length), imagePool.length - 1)

  return imagePool[Math.max(0, randomIndex)]
}

export const favoriteWorkoutFromApi = (favorite: ApiTabletWorkoutFavorite): FavoriteWorkout | null => {
  if (favorite.kind === 'template' && favorite.templateId) {
    return {
      favoriteId: favorite.favoriteId,
      kind: 'template',
      templateId: favorite.templateId,
    }
  }

  if (favorite.kind !== 'freestyle' || !isFreestyleImageKey(favorite.imageKey) || !favorite.workoutSession) {
    return null
  }

  try {
    const workoutSession = JSON.parse(favorite.workoutSession) as INRWorkoutSession
    if (!workoutSession || !Array.isArray(workoutSession.rounds)) return null

    const favoriteImage = FREESTYLE_FAVORITE_IMAGES[favorite.imageKey]
    return {
      favoriteId: favorite.favoriteId,
      favoriteImage,
      imageKey: favorite.imageKey,
      kind: 'freestyle',
      workoutSession: {
        ...workoutSession,
        favoriteId: favorite.favoriteId,
        favoriteImage,
        favoriteImageKey: favorite.imageKey,
      },
    }
  } catch {
    return null
  }
}

export const favoriteWorkoutsFromApi = (favorites: ApiTabletWorkoutFavorite[]): FavoriteWorkout[] =>
  favorites
    .map(favoriteWorkoutFromApi)
    .filter((favorite): favorite is FavoriteWorkout => favorite !== null)
    .slice(0, MAX_FAVORITE_WORKOUTS)

export const isWorkoutFavorite = (session: INRWorkoutSession, favorites: FavoriteWorkout[]): boolean => {
  if (session.name === WorkoutName.FREESTYLE) {
    return Boolean(session.favoriteId && favorites.some((favorite) => favorite.favoriteId === session.favoriteId))
  }

  return Boolean(session.id && favorites.some(
    (favorite) => favorite.kind === 'template' && favorite.templateId === session.id,
  ))
}

export const createFavoriteWorkoutInput = (
  session: INRWorkoutSession,
  favorites: FavoriteWorkout[],
  random: () => number = Math.random,
): TabletWorkoutFavoriteInput | null => {
  if (session.name !== WorkoutName.FREESTYLE) {
    return session.id ? { kind: 'template', templateId: session.id } : null
  }

  const existing = session.favoriteId
    ? favorites.find((favorite) => favorite.favoriteId === session.favoriteId)
    : undefined
  if (existing) {
    return { favoriteId: existing.favoriteId, kind: 'freestyle' }
  }

  const imageKey = session.favoriteImageKey && isFreestyleImageKey(session.favoriteImageKey)
    ? session.favoriteImageKey
    : pickFreestyleImageKey(favorites, random)

  return {
    favoriteId: session.favoriteId,
    imageKey,
    kind: 'freestyle',
    workoutSession: JSON.stringify(sanitizeFavoriteSession(session)),
  }
}
